import { Outlet } from "react-router-dom"
import { LayoutAppSidebar } from "./layout-app-sidebar"
import { LayoutAppHeader } from "./layout-app-header"
import { useModalStore, useOptionsStore } from "@/entities"
import { useEffect, useRef, useState } from "react"
import { Modal } from "@/shared/types"
import { ChangelogModal, OnboardingModal, MobileAppPromo } from "@/widgets"
import { CommonPlayer } from "@/features"
import { useAppSidebarStore } from "./model"
import {
  changelogControllerGetMyPost,
  ChangelogControllerGetMyPostPlatform,
} from "@/shared/api/generated"
import { GuestModeBanner } from "./guest-mode-banner"
import { useUserStore } from "@/entities"
import { useAutoTranslation } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS } from "@/shared/constants"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { CheckCircle2 } from "lucide-react"

const MOBILE_BREAKPOINT = 1200

const OUTSIDE_PADDING = 120 // header 60 + sidebar 60
const OUTSIDE_MOBILE_PADDING = 185 // header 60 + player 125
const GUEST_BANNER_HEIGHT = 44

const getInitialExtensionHandoff = (): "account" | "guest" | null => {
  const params = new URLSearchParams(window.location.search)
  if (params.get("extension_guest") === "1") return "guest"
  if (params.get("extension_connected") === "1") return "account"
  return null
}

export const LayoutApp = () => {
  const userOptions = useOptionsStore(s => s.data)
  const openModal = useModalStore(s => s.openModal)
  const isOpened = useAppSidebarStore(s => s.isOpened)
  const setIsOpened = useAppSidebarStore(s => s.setIsOpened)
  const isGuest = useUserStore(s => s.isGuest)
  const { t } = useAutoTranslation()
  const didRequestChangelog = useRef(false)
  const didOpenVoiceOnboarding = useRef(false)
  const [extensionHandoff, setExtensionHandoff] = useState<
    "account" | "guest" | null
  >(getInitialExtensionHandoff)

  const [containerHeight, setContainerHeight] = useState(
    (window.innerWidth > MOBILE_BREAKPOINT
      ? window.innerHeight - OUTSIDE_PADDING
      : window.innerHeight - OUTSIDE_MOBILE_PADDING) -
      (isGuest ? GUEST_BANNER_HEIGHT : 0)
  )

  if (!userOptions) {
    throw new Error("User options not found")
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const accountConnected = params.get("extension_connected") === "1"
    const guestConnected = params.get("extension_guest") === "1"
    if (!accountConnected && !guestConnected) return

    setExtensionHandoff(guestConnected ? "guest" : "account")
    params.delete("extension_connected")
    params.delete("extension_guest")
    const query = params.toString()
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    )
  }, [t])

  useEffect(() => {
    if (
      extensionHandoff ||
      !userOptions.needWebOnboarding ||
      didOpenVoiceOnboarding.current
    ) return

    didOpenVoiceOnboarding.current = true
    openModal(Modal.ONBOARDING, <OnboardingModal />)
  }, [extensionHandoff, openModal, userOptions.needWebOnboarding])

  useEffect(() => {
    if (extensionHandoff || userOptions.needWebOnboarding) return
    if (didRequestChangelog.current) return
    didRequestChangelog.current = true

    void changelogControllerGetMyPost({
      platform: ChangelogControllerGetMyPostPlatform.WEB,
    }).then(changelog => {
      if (changelog.content) {
        openModal(Modal.CHANGELOG, <ChangelogModal changelog={changelog} />)
      }
    })
  }, [extensionHandoff, openModal, userOptions.needWebOnboarding])

  useEffect(() => {
    const refreshOptions = () => {
      if (!document.hidden) {
        void useOptionsStore.getState().invalidateOptions()
      }
    }
    window.addEventListener("focus", refreshOptions)
    document.addEventListener("visibilitychange", refreshOptions)
    return () => {
      window.removeEventListener("focus", refreshOptions)
      document.removeEventListener("visibilitychange", refreshOptions)
    }
  }, [])

  useEffect(() => {
    const handler = () => {
      const height =
        window.innerWidth > MOBILE_BREAKPOINT
          ? window.innerHeight - OUTSIDE_PADDING - (isGuest ? GUEST_BANNER_HEIGHT : 0)
          : window.innerHeight - OUTSIDE_MOBILE_PADDING - (isGuest ? GUEST_BANNER_HEIGHT : 0)
      setContainerHeight(height)
    }
    window.addEventListener("resize", handler)
    return () => {
      window.removeEventListener("resize", handler)
    }
  }, [isGuest])

  return (
    <main className="flex h-screen">
      <Dialog
        open={extensionHandoff !== null}
        onOpenChange={open => {
          if (!open) setExtensionHandoff(null)
        }}
      >
        <DialogContent
          showCloseButton={false}
        >
          <DialogHeader className="items-center text-center sm:text-center">
            <div className="mb-2 grid size-14 place-items-center rounded-full bg-[#2066e8]/15 text-[#2066e8]">
              <CheckCircle2 className="size-8" />
            </div>
            <DialogTitle>
              {extensionHandoff === "guest"
                ? t(DISCOVERED_TRANSLATION_KEYS.EXTENSION_GUEST_CONNECTED)
                : t(DISCOVERED_TRANSLATION_KEYS.EXTENSION_ACCOUNT_CONNECTED)}
            </DialogTitle>
            <DialogDescription className="max-w-md text-center leading-6">
              {extensionHandoff === "guest"
                ? t(DISCOVERED_TRANSLATION_KEYS.GUEST_LIBRARY_SYNC_DESCRIPTION)
                : t(DISCOVERED_TRANSLATION_KEYS.UNIFIED_LIBRARY_AVAILABLE)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              className="min-w-28 bg-[#2066e8] text-white hover:bg-[#1857ca]"
              onClick={() => setExtensionHandoff(null)}
            >
              {t(DISCOVERED_TRANSLATION_KEYS.OK)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BACKDROP FOR MOBILE MENU */}
      {isOpened && (
        <div
          className="fixed inset-0 z-50 block bg-black/50 min-[1366px]:hidden"
          onClick={() => setIsOpened(false)}
        ></div>
      )}

      <LayoutAppSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <GuestModeBanner />
        <LayoutAppHeader />
        <div
          className="relative overflow-y-auto [scrollbar-gutter:stable]"
          style={{ height: containerHeight }}
        >
          <Outlet />
        </div>
      </div>

      <CommonPlayer />
      <MobileAppPromo />
    </main>
  )
}
